import { useState, useEffect } from "react";
import {
  EditableInputComponent,
  EditableSelectComponent,
  EditableTextareaComponent,
  SectionHeader,
} from "components";
import { ArrowUpRight, Bulb } from "assets/icons";
import { toast } from "react-toastify";
import { EnergyPerformanceInfo } from "shared/types/energy-performance";
import { UnitDTO } from "shared/types/unit";
import { BuildingDTO } from "shared/types/building";
import EPCsComponent from "./epcsComponent";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { EPC, VentilationSystemType } from "shared/types/epc";
import { Chart } from "chart.js";

interface EnergyPerformanceInfoProps {
  energyPerformanceInfo: EnergyPerformanceInfo;
  handleEnergyPerformanceInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  parentEditMode?: boolean;
  unit: UnitDTO | undefined | null;
  building: BuildingDTO | undefined | null;
}

const EnergyPerformanceInfoComponent = ({
  energyPerformanceInfo,
  handleCloseParent,
  handleSaveInParent,
  handleEnergyPerformanceInfoChangeInParent,
  parentEditMode = true,
  unit,
  building,
}: EnergyPerformanceInfoProps) => {
  const [editMode, setEditMode] = useState<boolean>(parentEditMode);
  const [editedEnergyPerformanceInfo, setEditedEnergyPerformanceInfo] =
    useState<EnergyPerformanceInfo | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [uValues, setUValues] = useState<any>();
  const [ventilationSystemType, setVentilationSystemType] = useState<
    VentilationSystemType | undefined | null
  >(undefined); // VentilationSystemType[energyPerformanceInfo.ventilationSystemType
  const { session } = useSession();
  useEffect(() => {
    let latestEpc = null;
    let epcs: EPC[] = [];

    // Check if the unit has energy performance info and EPCs
    if (unit?.energyPerformanceInfo?.epcs) {
      epcs = unit.energyPerformanceInfo.epcs;
    }
    // If not, check if the building has energy performance info and EPCs
    else if (building?.energyPerformanceInfo?.epcs) {
      epcs = building.energyPerformanceInfo.epcs;
    }

    // Sort the EPCs by documentDate and get the latest one
    latestEpc = epcs.sort(
      (a, b) =>
        new Date(b.fileLog?.documentDate).getTime() -
        new Date(a.fileLog?.documentDate).getTime(),
    )[0];

    // Set the latest EPC and its U-values if available
    if (latestEpc) {
      setUValues({
        walls: latestEpc?.uValueWalls,
        windows: latestEpc?.uValueWindows,
        glazing: latestEpc?.uValueGlazing,
        // ...other U-values
      });

      setVentilationSystemType(
        latestEpc?.ventilationSystemType as VentilationSystemType,
      );
    }
  }, [unit, building]);
  useEffect(() => {
    setEditedEnergyPerformanceInfo(energyPerformanceInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch
  useEffect(() => {
    setEditMode(parentEditMode);
  }, [parentEditMode]);
  const handleSave = async () => {
    setHasUnsavedChanges(false);

    if (handleSaveInParent) {
      handleSaveInParent();
    } else {
      try {
        await fetchWithAuth(
          `${API_URL}/energy/energy-performance-info`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(editedEnergyPerformanceInfo),
          },
          session,
        );
        toast.success("Energieprestatiegegevens succesvol bijgewerkt!");
      } catch (e) {
        console.error(e);
        toast.error(
          "Er is een fout opgetreden bij het bijwerken van de energieprestatiegegevens.",
        );
      }
    }
  };
  const handleEditClick = () => {
    setEditMode(!editMode);
  };
  const onUpdateRemarks = (remarks: string) => {
    setEditedEnergyPerformanceInfo((prev: any) => ({ ...prev, remarks }));
    setHasUnsavedChanges(true);
  };
  const updateEnergyPerformanceInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedEnergyPerformanceInfo((prev: any) => ({
      ...prev,
      ...updatedFields,
    }));

    if (handleEnergyPerformanceInfoChangeInParent) {
      handleEnergyPerformanceInfoChangeInParent({
        ...energyPerformanceInfo,
        ...updatedFields,
      });
    }
  };
  const getStatusColor = (status: any) => {
    switch (status) {
      case VentilationSystemType.C:
      case VentilationSystemType.C_PLUS:
      case VentilationSystemType.D:
        return "bg-green-500";
      case VentilationSystemType.A:
      case VentilationSystemType.B:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };
  useEffect(() => {
    if (uValues) {
      // Assuming uValues state has the structure { walls: number, windows: number, glazing: number }
      createUValueChart("wallsChart", uValues.walls, 0.24, "U-waarde Muren");
      createUValueChart(
        "windowsChart",
        uValues.windows,
        1.5,
        "U-waarde Vensters",
      );
      createUValueChart(
        "glazingChart",
        uValues.glazing,
        1.0,
        "U-waarde Beglazing",
      );
    }
  }, [uValues]);
  const createUValueChart = (
    canvasId: any,
    actualValue: any,
    targetValue: any,
    label: any,
  ) => {
    const ctx = (document as any).getElementById(canvasId).getContext("2d");

    // Set the actual max value of the scale to the actual value.
    const scaleMax = actualValue / (targetValue / actualValue);

    // Destroy the previous chart instance if it exists
    if (Chart.getChart(canvasId)) {
      (Chart as any).getChart(canvasId).destroy();
    }

    new Chart(ctx, {
      type: "bar",
      data: {
        labels: [""],
        datasets: [
          {
            label: "Huidige U-waarde",
            data: [actualValue],
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        indexAxis: "y",
        scales: {
          x: {
            beginAtZero: true,
            max: scaleMax, // This will be the actual max value of the scale
            ticks: {
              // Custom tick labels
              callback: function (value, index, ticks) {
                if (index === ticks.length - 1) {
                  console.log(value);
                  // The last tick will be the target value
                  return `Doelstelling 2050: ${targetValue} W/(m²K)`;
                }
              },
            },
          },
          y: {
            beginAtZero: true,
          },
        },
        plugins: {
          title: {
            display: true,
            text: label, // Use the label parameter as the title
            padding: {
              top: 10,
              bottom: 30,
            },
            font: {
              size: 16,
            },
          },
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                label += Math.round((context.parsed?.x || 0) * 100) / 100;
                label += " W/(m²K)";
                return label;
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
    });
  };
  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Energie prestaties"
        icon={<Bulb />}
        editMode={editMode}
        onEditClick={handleEditClick}
        onSaveClick={handleSave}
        sticky={false}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={() => {
          if (handleCloseParent) handleCloseParent();
        }}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30`}
              />
              <p>Opmerkingen op de energieprestaties?</p>
            </header>
            <EditableTextareaComponent
              label="Opmerkingen"
              value={editedEnergyPerformanceInfo?.remarks || ""}
              isLoading={false}
              editMode={editMode}
              onChange={onUpdateRemarks}
            />
          </div>
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  ventilationSystemType,
                )}`}
              />
              <p>Type ventilatiesysteem</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze"
              value={ventilationSystemType ? ventilationSystemType : ""}
              options={Object.values(VentilationSystemType).map((value) => ({
                label: value,
                value,
              }))}
              editMode={false}
              onChange={(newStatus) =>
                updateEnergyPerformanceInfo({
                  ventilationSystemType: newStatus,
                })
              }
            />
          </div>
          <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
            <header className="flex items-center justify-between text-sm font-medium">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30`}
                />
                <p>Zonnekaart</p>
              </div>
              <a
                href="https://apps.energiesparen.be/zonnekaart"
                target="_blank"
                className="flex items-center gap-0.5 text-moss-primary"
              >
                Bekijk zonnekaart <ArrowUpRight className="h-[18px] w-[18px]" />
              </a>
            </header>
            <EditableInputComponent
              label="Vul waarde in"
              value={"" as any}
              isLoading={false}
              editMode={false}
              onChange={(value) =>
                updateEnergyPerformanceInfo({ zonnekaart: value })
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
          <div>
            <canvas id="wallsChart"></canvas>
          </div>
          <div>
            <canvas id="windowsChart"></canvas>
          </div>
          <div>
            <canvas id="glazingChart"></canvas>
          </div>
        </div>
        <EPCsComponent
          energyPerformanceInfoId={editedEnergyPerformanceInfo?.id}
          unit={unit}
          building={building}
        />
      </div>
    </section>
  );
};

export default EnergyPerformanceInfoComponent;
